import { PrintOutlined } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import { Color } from '@tiptap/extension-color';
import Heading from '@tiptap/extension-heading';
import { Highlight } from '@tiptap/extension-highlight';
import { Image } from '@tiptap/extension-image';
import { Link } from '@tiptap/extension-link';
import ListItem from '@tiptap/extension-list-item';
import { Subscript } from '@tiptap/extension-subscript';
import { Superscript } from '@tiptap/extension-superscript';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TextAlign } from '@tiptap/extension-text-align';
import { BackgroundColor, TextStyle } from '@tiptap/extension-text-style';
import { Underline } from '@tiptap/extension-underline';
import { EditorContent, useEditor, type Editor as TiptapEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useCallback, useEffect, useRef, useState } from 'react';

import { EditorToolbar } from './EditorToolbar';
import { PrintPreview } from './PrintPreview';
import './listStyles.css';
import './pageBreakStyles.css';
import './tableStyles.css';

import { buildDocumentContentCss, getDocumentCanvasStyle } from './documentContentStyles';
import { DocumentFontFamily } from './DocumentFontFamily';
import { FontSize } from './FontSizeExtension';
import { PageBreak } from './PageBreakExtension';
import {
    EnhancedParagraph,
    EnhancedTableCell,
    EnhancedTableHeader,
    LineHeight,
    createEnhancedHeading,
    createEnhancedListItem,
    createEnhancedTable,
} from './PasteExtensions';
import { processPastedContent } from './pasteUtils';
import { PlaceholderExtension } from './PlaceholderExtension';
import { DEFAULT_BRANDING, PAGE_SIZES } from 'config/defaultBranding';
import type { BrandingSettings, PricingConfiguration, TemplateType } from 'types';

interface RichTextEditorProps {
    content: string;
    onChange: (html: string) => void;
    editable?: boolean;
    pricingConfig?: PricingConfiguration;
    branding?: BrandingSettings;
    onBrandingChange?: (branding: BrandingSettings) => void;
    onBrandingReset?: () => void;
    onPlaceholdersChange?: (placeholders: string[]) => void;
    templateType?: TemplateType;
    showPrintPreviewButton?: boolean;
}
/**
 * Inserts page breaks into HTML content based on line/element count
 * Simulates pages by counting block elements (paragraphs, headings, list items)
 */
function insertPageBreaksByLines(html: string): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const body = doc.body;

    // Configuration
    const ELEMENTS_PER_PAGE = 35; // Adjust this number based on your needs

    // Get all block-level elements (p, h1-h6, li, div)
    const blockElements = Array.from(
        body.querySelectorAll('p, h1, h2, h3, h4, h5, h6, li, div:not(.page-break)')
    );

    console.log(`[PageBreak] Total block elements: ${blockElements.length}`);
    console.log(`[PageBreak] Will insert breaks every ${ELEMENTS_PER_PAGE} elements`);

    let elementCount = 0;
    let pageBreaksInserted = 0;

    blockElements.forEach((element, index) => {
        elementCount++;

        // Insert page break every N elements
        if (elementCount >= ELEMENTS_PER_PAGE && index < blockElements.length - 1) {
            // Create page break element
            const pageBreak = doc.createElement('div');
            pageBreak.setAttribute('data-type', 'page-break');
            pageBreak.className = 'page-break';

            // Insert after current element
            element.parentNode?.insertBefore(pageBreak, element.nextSibling);

            // Reset counter
            elementCount = 0;
            pageBreaksInserted++;

            console.log(
                `[PageBreak] Inserted break #${pageBreaksInserted} after element:`,
                element.textContent?.substring(0, 50)
            );
        }
    });

    console.log(`[PageBreak] Total page breaks inserted: ${pageBreaksInserted}`);

    return body.innerHTML;
}
export function RichTextEditor({
    content,
    onChange,
    editable = true,
    pricingConfig,
    branding,
    onBrandingChange,
    onBrandingReset,
    onPlaceholdersChange,
    templateType = 'visa-application',
    showPrintPreviewButton = true,
}: RichTextEditorProps) {
    const [showPrintPreview, setShowPrintPreview] = useState(false);
    const lastPlaceholdersRef = useRef<string[]>([]);

    // Memoized placeholder extraction function
    const extractPlaceholders = useCallback((editor: TiptapEditor) => {
        const placeholders: string[] = [];
        const allNodes: Array<{
            type: string;
            attrs: Record<string, unknown>;
            text?: string;
            pos: number;
        }> = [];

        editor.state.doc.descendants(
            (
                node: { type: { name: string }; attrs: Record<string, unknown>; text?: string },
                pos: number
            ) => {
                allNodes.push({
                    type: node.type.name,
                    attrs: node.attrs,
                    text: node.text,
                    pos,
                });

                if (node.type.name === 'placeholder' && node.attrs.token) {
                    placeholders.push(node.attrs.token as string);
                }
            }
        );

        // Debug: log all nodes to see what's in the document
        console.log('[PlaceholderDetection] Document structure:', {
            totalNodes: allNodes.length,
            nodes: allNodes.slice(0, 20), // First 20 nodes
            placeholderNodes: allNodes.filter((n) => n.type === 'placeholder'),
        });

        // Always extract from HTML content to catch plain text placeholders
        const html = editor.getHTML();

        // Try to extract from anchor tags (placeholders might be rendered as links)
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const anchorTags = doc.querySelectorAll('a[href^="http://"], a[href^="https://"]');

        anchorTags.forEach((anchor) => {
            const href = anchor.getAttribute('href') || '';
            const text = anchor.textContent?.trim() || '';

            // Extract token from href (remove http:// or https://)
            const tokenFromHref = href.replace(/^https?:\/\//, '').trim();

            // Use href token if it matches the text content, otherwise use text
            const token = tokenFromHref && tokenFromHref === text ? tokenFromHref : text;

            if (token && !placeholders.includes(token)) {
                const normalized =
                    token.startsWith('{{') && token.endsWith('}}') ? token : `{{${token}}}`;
                placeholders.push(normalized);
            }
        });

        // Also extract using {{...}} pattern on text content (not raw HTML) to avoid
        // matching tokens that have been auto-linked (e.g. .org_name wrapped in <a> tags)
        const textContent = doc.body.textContent || '';
        const placeholderRegex = /\{\{([^}]+)\}\}/g;
        let match;
        while ((match = placeholderRegex.exec(textContent)) !== null) {
            const token = `{{${match[1].trim()}}}`;
            if (!placeholders.includes(token)) {
                placeholders.push(token);
            }
        }

        console.log('[PlaceholderDetection] Final extracted placeholders:', {
            count: placeholders.length,
            tokens: placeholders,
        });

        return placeholders;
    }, []);

    // Helper to check and notify if placeholders changed
    const notifyPlaceholdersChange = useCallback(
        (placeholders: string[], source: string) => {
            const lastPlaceholders = lastPlaceholdersRef.current;
            const hasChanged =
                lastPlaceholders.length !== placeholders.length ||
                !lastPlaceholders.every((p, i) => p === placeholders[i]);

            console.log(`[PlaceholderDetection] ${source}: Checking placeholders`, {
                previous: {
                    count: lastPlaceholders.length,
                    tokens: lastPlaceholders,
                },
                current: {
                    count: placeholders.length,
                    tokens: placeholders,
                },
                hasChanged,
            });

            if (!hasChanged) {
                console.log(
                    `[PlaceholderDetection] ${source}: No change detected, skipping callback`
                );
                return;
            }

            console.log(`[PlaceholderDetection] ${source}: Placeholders changed! Updating...`, {
                count: placeholders.length,
                tokens: placeholders,
            });

            lastPlaceholdersRef.current = placeholders;
            if (onPlaceholdersChange) {
                onPlaceholdersChange(placeholders);
            }
        },
        [onPlaceholdersChange]
    );

    const editor = useEditor({
        extensions: [
            // Use StarterKit but override specific extensions with enhanced versions
            StarterKit.configure({
                // Disable paragraph, heading, and listItem as we're using enhanced versions
                paragraph: false,
                heading: false,
                listItem: false,
            }),
            // Add our enhanced versions
            EnhancedParagraph,
            createEnhancedHeading(Heading),
            createEnhancedListItem(ListItem),
            // Enhanced table extensions
            createEnhancedTable(Table).configure({
                resizable: true,
                HTMLAttributes: {
                    class: 'editor-table',
                },
            }),
            TableRow,
            EnhancedTableCell.configure({
                HTMLAttributes: {
                    class: 'editor-table-cell',
                },
            }),
            EnhancedTableHeader.configure({
                HTMLAttributes: {
                    class: 'editor-table-header',
                },
            }),
            // Standard extensions
            Image,
            Underline,
            TextStyle,
            Color,
            BackgroundColor,
            DocumentFontFamily,
            FontSize,
            Highlight.configure({
                multicolor: true,
            }),
            Subscript,
            Superscript,
            TextAlign.configure({
                types: ['heading', 'paragraph'],
            }),
            Link.configure({
                openOnClick: false,
                autolink: false,
                linkOnPaste: false,
                HTMLAttributes: {
                    class: 'text-primary underline cursor-pointer',
                },
            }),
            // Line height support
            LineHeight,
            // Custom extensions
            PlaceholderExtension,
            PageBreak,
        ],
        content,
        editable,
        onUpdate: ({ editor }) => {
            onChange(editor.getHTML());

            // Extract placeholder tokens from the editor document
            if (onPlaceholdersChange) {
                const placeholders = extractPlaceholders(editor);
                console.log(
                    '[PlaceholderDetection] onUpdate: Editor updated, checking placeholders',
                    {
                        placeholderCount: placeholders.length,
                        tokens: placeholders,
                    }
                );
                notifyPlaceholdersChange(placeholders, 'onUpdate');
            }
        },
        editorProps: {
            attributes: {
                class: 'max-w-none focus:outline-none',
            },

            transformPastedHTML(html) {
                console.log('[Paste] Original HTML length:', html.length);
                console.log('[Paste] Original HTML preview:', html.substring(0, 200));

                // Use the unified paste processing function that handles both HTML and plain text
                let processedHTML = processPastedContent(html);

                // ADD THIS: Insert page breaks based on line count
                processedHTML = insertPageBreaksByLines(processedHTML);

                console.log('[Paste] Final HTML preview:', processedHTML.substring(0, 200));
                console.log('[Paste] Has lists:', (processedHTML.match(/<[uo]l/g) || []).length);
                console.log(
                    '[Paste] Has headings:',
                    (processedHTML.match(/<h[1-6]/g) || []).length
                );
                console.log('[Paste] Has tables:', (processedHTML.match(/<table/g) || []).length);
                console.log(
                    '[Paste] Has page breaks:',
                    (processedHTML.match(/page-break/g) || []).length
                );

                // Trigger placeholder detection after paste
                setTimeout(() => {
                    if (onPlaceholdersChange && editor) {
                        const placeholders = extractPlaceholders(editor);
                        notifyPlaceholdersChange(placeholders, 'paste');
                    }
                }, 100);

                return processedHTML;
            },
        },
    });

    useEffect(() => {
        if (editor && content !== editor.getHTML()) {
            editor.commands.setContent(content);

            // Extract placeholders after content is set
            if (onPlaceholdersChange) {
                // Use setTimeout to ensure content is fully set
                setTimeout(() => {
                    const placeholders = extractPlaceholders(editor);
                    notifyPlaceholdersChange(placeholders, 'useEffect (content change)');
                }, 0);
            }
        }
    }, [content, editor, onPlaceholdersChange, extractPlaceholders, notifyPlaceholdersChange]);

    // Single useEffect to handle editor ready (initial load only)
    useEffect(() => {
        if (editor && onPlaceholdersChange) {
            // Extract on initial load only
            const placeholders = extractPlaceholders(editor);
            notifyPlaceholdersChange(placeholders, 'useEffect (editor ready)');

            // Don't add editor.on('update') listener here - onUpdate callback already handles it
            // This prevents duplicate processing of the same update event
        }
    }, [editor, onPlaceholdersChange, extractPlaceholders, notifyPlaceholdersChange]);

    if (!editor) {
        return null;
    }

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    minWidth: 0,
                    bgcolor: '#f5f5f5',
                }}
            >
                {editable && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            bgcolor: 'background.paper',
                            minWidth: 0,
                            borderBottom: '1px solid #e8eaed',
                        }}
                    >
                        <Box sx={{ flex: 1, minWidth: 0, overflowX: 'auto', overflowY: 'visible' }}>
                            <EditorToolbar
                                editor={editor}
                                pricingConfig={pricingConfig}
                                branding={branding}
                                onBrandingChange={onBrandingChange}
                                onBrandingReset={onBrandingReset}
                                templateType={templateType}
                            />
                        </Box>
                        {showPrintPreviewButton && (
                            <Box sx={{ px: 1.5, py: 0.75, borderLeft: '1px solid #e5e7eb' }}>
                                <Button
                                    variant="text"
                                    color="inherit"
                                    size="small"
                                    onClick={() => setShowPrintPreview(true)}
                                    startIcon={<PrintOutlined sx={{ fontSize: 16 }} />}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        height: 28,
                                        minHeight: 28,
                                        px: 1.25,
                                        borderRadius: '4px',
                                        color: '#444746',
                                        boxShadow: 'none',
                                        '&:hover': {
                                            bgcolor: 'rgba(68, 71, 70, 0.08)',
                                            boxShadow: 'none',
                                        },
                                    }}
                                >
                                    Print Preview
                                </Button>
                            </Box>
                        )}
                    </Box>
                )}
                {!editable && showPrintPreviewButton && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            px: 1.5,
                            py: 0.75,
                            bgcolor: 'background.paper',
                            borderBottom: '1px solid #e5e7eb',
                        }}
                    >
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setShowPrintPreview(true)}
                            startIcon={<PrintOutlined sx={{ fontSize: 16 }} />}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                            Print Preview
                        </Button>
                    </Box>
                )}
                <Box
                    sx={{
                        flex: 1,
                        overflow: 'auto',
                        minWidth: 0,
                        bgcolor: '#f5f5f5',
                        px: 2,
                        py: 3,
                    }}
                >
                    <Box
                        className="editor-content"
                        sx={{
                            mx: 'auto',
                            width: PAGE_SIZES[branding?.pageSize || 'letter'].width,
                            minHeight: PAGE_SIZES[branding?.pageSize || 'letter'].height,
                            boxShadow:
                                '0 1px 3px rgba(60, 64, 67, 0.3), 0 4px 8px 3px rgba(60, 64, 67, 0.15)',
                            ...getDocumentCanvasStyle(branding ?? DEFAULT_BRANDING),
                        }}
                    >
                        <EditorContent editor={editor} className="max-w-none" />
                    </Box>
                    <style>
                        {buildDocumentContentCss(
                            '.editor-content .ProseMirror',
                            branding ?? DEFAULT_BRANDING
                        )}
                    </style>
                </Box>
            </Box>

            {showPrintPreviewButton && (
                <PrintPreview
                    open={showPrintPreview}
                    onOpenChange={setShowPrintPreview}
                    content={editor.getHTML()}
                    branding={branding}
                />
            )}
        </>
    );
}
